import { LitheFC, LitheSpecificElement } from '@wheelbox/lithejs-engine';
import withTranslations from '@wheelbox/lithejs-i18n';
import {
  CpuIcon,
  RouteIcon,
  DatabaseIcon,
  GlobeIcon,
  LayoutIcon,
  FileTextIcon,
  SmileIcon,
  RocketIcon,
} from '@wheelbox/lithejs-lucide';

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@components/ui';
import { createId } from '@utils/index';

import { type DocMeta } from '@lib/docs-loader';

type DocsLandingViewProps = {
  sections: DocMeta[];
  onselect: (filename: string) => void;
};

const PACKAGE_ICONS: Record<string, typeof CpuIcon> = {
  '01-engine': CpuIcon,
  '02-router': RouteIcon,
  '03-state': DatabaseIcon,
  '04-i18n': GlobeIcon,
  '05-ui': LayoutIcon,
  '06-markdown': FileTextIcon,
  '07-lucide': SmileIcon,
  '08-create-app': RocketIcon,
};

const DocsLandingView: LitheFC<DocsLandingViewProps> = ({
  id = createId(),
  testId = 'docs-landing',
  sections,
  onselect,
}) => {
  const t = withTranslations('Docs');

  const getIconForSection = (filename: string) => {
    const IconComponent = PACKAGE_ICONS[filename] || FileTextIcon;
    return IconComponent({ size: 24, classes: 'text-primary' });
  };

  const renderPackageCard = (section: DocMeta): LitheSpecificElement => {
    const descriptionKey = `landing.packages.${section.filename}.description`;
    const description = t(descriptionKey);

    return [
      'div',
      {
        class: [
          'cursor-pointer transition-all duration-200',
          'hover:shadow-md hover:border-primary/30 hover:-translate-y-0.5',
          'group rounded-xl',
        ],
        onclick: () => onselect(section.filename),
      },
      Card(
        {},
        CardHeader({ classes: 'pb-2' }, [
          'div',
          { class: 'flex items-center gap-3' },
          [
            'div',
            {
              class:
                'p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors',
            },
            getIconForSection(section.filename),
          ],
          CardTitle({ classes: 'text-lg' }, section.title),
        ]),
        CardContent(
          { classes: 'pt-0' },
          CardDescription(
            { classes: 'text-sm leading-relaxed' },
            description !== descriptionKey ? description : ''
          )
        )
      ),
    ];
  };

  const renderHeroSection = (): LitheSpecificElement => [
    'div',
    { class: 'text-center mb-12' },
    [
      'h1',
      { class: 'text-4xl font-bold text-foreground mb-4' },
      t('landing.title'),
    ],
    [
      'p',
      { class: 'text-lg text-muted-foreground max-w-2xl mx-auto' },
      t('landing.description'),
    ],
  ];

  const renderPackagesGrid = (): LitheSpecificElement => [
    'div',
    {
      class:
        'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4',
    },
    ...sections.map((section) => renderPackageCard(section)),
  ];

  const renderGettingStarted = (): LitheSpecificElement => [
    'div',
    { class: 'mt-12 p-6 bg-muted/50 rounded-xl border' },
    [
      'h2',
      { class: 'text-xl font-semibold mb-3 text-foreground' },
      t('landing.getting_started.title'),
    ],
    [
      'p',
      { class: 'text-muted-foreground mb-4' },
      t('landing.getting_started.description'),
    ],
    [
      'pre',
      {
        class:
          'bg-slate-900 text-slate-50 p-4 rounded-lg overflow-x-auto text-sm',
      },
      ['code', {}, 'npx @wheelbox/lithejs-create-app my-app'],
    ],
  ];

  return [
    'div',
    {
      id,
      'data-testid': testId,
      class: 'docs-landing max-w-6xl mx-auto',
    },
    renderHeroSection(),
    renderPackagesGrid(),
    renderGettingStarted(),
  ];
};

export default DocsLandingView;
